import * as React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, styled, Typography } from "@mui/material";

import { LaunchWorkflowForm } from "src/domain/core/workflow";

interface LaunchDialogProps {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  template?: Template;
  templates: Template[];
  blueprintId: number;
  nodes: Nodes;
  blueprints: WorkflowBlueprintDesc[];
  disks?: Disk[];
  disksError?: Error;
  groups?: Group[];
  source?: string;
}

const TitleText = styled(Typography)(() => ({
  fontSize: "20px",
  textAlign: "left",
}));

const SubText = styled(Typography)(() => ({
  fontSize: "14px",
  textAlign: "left",
}));

export const LaunchDialog: React.FC<LaunchDialogProps> = ({
  modalOpen,
  setModalOpen,
  template,
  templates,
  blueprintId,
  groups,
  nodes,
  blueprints,
  disks,
  disksError,
  source,
}) => {
  const getInitialBlueprintId = () => {
    if (!template && !blueprintId) {
      return blueprints[0]?.id;
    }

    if (template && template.blueprintId) {
      return template.blueprintId;
    }

    return blueprintId;
  };

  const [bpId, setBlueprintId] = React.useState(getInitialBlueprintId());

  const blueprintName = React.useMemo(() => {
    if (!blueprints || blueprints.length == 0) {
      return "";
    }

    const relevantBlueprint = blueprints.find((bp) => {
      return bp.id === bpId;
    });

    if (!relevantBlueprint) {
      return blueprints[0].name;
    }

    return relevantBlueprint.name;
  }, [bpId, blueprints]);

  return (
    <Dialog
      open={modalOpen}
      onClose={(event, reason) => {
        if (reason && reason == "backdropClick") return;
        setModalOpen(false);
      }}
      fullWidth={true}
      maxWidth={"md"}
    >
      <DialogTitle
        sx={{
          paddingLeft: "33px",
          color: "#ffffff",
          backgroundColor: (theme) => theme.palette.primary.main,
        }}
      >
        <Grid container alignItems="center">
          <Grid item container justifyContent="flex-start" xs={11}>
            <Stack spacing={0}>
              {source === "create-template" ? (
                <TitleText>Create New Organization Template</TitleText>
              ) : (
                <TitleText>Launch {template ? template.templateName : blueprintName}</TitleText>
              )}
              <SubText>Blueprint: {blueprintName}</SubText>
            </Stack>
          </Grid>
          <Grid item container justifyContent="flex-end" xs={1}>
            <IconButton aria-label="close" onClick={() => setModalOpen(false)}>
              <CloseIcon sx={{ color: "#ffffff" }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <LaunchWorkflowForm
          blueprintId={bpId}
          nodeData={{
            head_node_instance_types: nodes?.cpus?.head,
            compute_node_instance_types: nodes?.cpus?.compute,
            gpu_head_node_instance_types: nodes?.gpus?.head,
            gpu_compute_node_instance_types: nodes?.gpus?.compute,
          }}
          blueprints={blueprints}
          setBlueprintId={setBlueprintId}
          groups={groups}
          template={template}
          templates={templates}
          close={() => setModalOpen(false)}
          disks={disks}
          disksError={disksError}
          source={source}
        />
      </DialogContent>
    </Dialog>
  );
};
